import type { FilterField, FilterOperator, FilterState } from '@dott/core';

/**
 * The list surfaces a saved view can belong to — the value of
 * `SavedView.context`, mirroring the server's `SavedViewContext`.
 */
export type SavedViewSurface = 'team-issues' | 'requests';

const SURFACE_LABELS: Record<SavedViewSurface, string> = {
  'team-issues': 'Issues',
  'requests': 'Requests',
};

/** Human label for a view's surface (the manage page's "Surface" column). */
export const resolveSurfaceLabel = (context: string): string =>
  SURFACE_LABELS[context as SavedViewSurface] ?? context;

const kebab = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/**
 * The URL segment of a saved view: `<kebab-name>-<slugId>`. Only the trailing
 * `slugId` is authoritative — the server extracts it and ignores the name half
 * — so renaming a view keeps every previously shared link working.
 */
export const savedViewSlug = (view: { name: string; slugId: string }): string => {
  const prefix = kebab(view.name);

  return prefix ? `${prefix}-${view.slugId}` : view.slugId;
};

/**
 * Canonical JSON of a filter state, with the TOP-LEVEL KEYS SORTED.
 *
 * Deviation ("does what I'm looking at still match the view?") is answered by
 * comparing two of these, never by comparing raw strings. Two reasons, both
 * load-bearing:
 *
 * 1. `SavedView.filters` is stored as `jsonb`, and Postgres does not preserve
 *    object key order — a value written as `{kind, op, values}` reads back as
 *    `{op, kind, values}`. Running both sides through `parseFilterState` first
 *    rebuilds every `FilterValue` in a fixed property order, which fixes that.
 * 2. `parseFilterState` still preserves the order it *iterates* the object in,
 *    and the chip UI inserts fields in the order the user picked them while
 *    jsonb returns them in its own. Without sorting the top-level keys,
 *    `{priority, status}` and `{status, priority}` — the same filter — compare
 *    unequal and the Save button appears on an untouched view forever.
 */
export const canonicalFilterState = (state: FilterState): string =>
  JSON.stringify(
    Object.fromEntries(Object.entries(state).sort(([a], [b]) => a.localeCompare(b))),
  );

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  isNot: 'is not',
  eq: 'is',
  between: 'between',
  gte: 'at least',
  lte: 'at most',
  before: 'before',
  after: 'after',
};

/**
 * A human, read-only summary of a filter state — one line per chip, in the
 * order the chips render. Used by the save modal so the user can see what they
 * are about to store instead of trusting the button.
 *
 * Reference fields resolve their labels over the network; a failed lookup falls
 * back to the raw ids rather than dropping the line (the summary is
 * informational, and a silent omission would misrepresent what gets saved).
 */
export const describeFilterState = async (
  state: FilterState,
  fields: FilterField[],
): Promise<string[]> => {
  const fieldByKey = new Map(fields.map((field) => [field.key, field]));
  const lines: string[] = [];

  for (const [key, value] of Object.entries(state)) {
    const field = fieldByKey.get(key);

    if (!field) {
      continue;
    }

    const operator = OPERATOR_LABELS[value.op];

    if (value.kind === 'enum') {
      const labelByValue = new Map(
        field.type === 'enum' ? field.options.map((option) => [option.value, option.label]) : [],
      );

      lines.push(
        `${field.label} ${operator} ${value.values
          .map((item) => labelByValue.get(item) ?? item)
          .join(', ')}`,
      );

      continue;
    }

    if (value.kind === 'reference') {
      let labels = value.values;

      if (field.type === 'reference') {
        try {
          const resolved = await field.resolveLabels(value.values);
          const labelById = new Map(resolved.map((option) => [option.value, option.label]));

          labels = value.values.map((item) => labelById.get(item) ?? item);
        } catch (error) {
          console.error('[saved-views] Failed to resolve filter labels:', error);
        }
      }

      lines.push(`${field.label} ${operator} ${labels.join(', ')}`);

      continue;
    }

    lines.push(
      value.op === 'between'
        ? `${field.label} ${operator} ${value.from} and ${value.to}`
        : `${field.label} ${operator} ${value.value}`,
    );
  }

  return lines;
};
